import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Zap,
  Database,
  BookOpen,
  Gauge,
  Building2,
  Trophy,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { useCountUp } from '@/hooks/useCountUp';
import { useLocalized } from '@/hooks/useLocalized';
import { useSeo } from '@/hooks/useSeo';
import { formatNumber } from '@/lib/format';
import { TOPICS, topicPath } from '@/data/topics';
import { problemsApi, statsApi } from '@/api/endpoints';
import type { ApiProblemTopic, PublicStatsResponse, TopicCountResponse } from '@/api/types';
import { useInitialData } from '@/ssr/initialData';

function toCountMap(rows: TopicCountResponse[]): Partial<Record<ApiProblemTopic, number>> {
  const map: Partial<Record<ApiProblemTopic, number>> = {};
  for (const row of rows) map[row.topic] = row.count;
  return map;
}

const FEATURES = [
  { key: 'schemas', Icon: Database },
  { key: 'dialects', Icon: Zap },
  { key: 'explanations', Icon: BookOpen },
  { key: 'performance', Icon: Gauge },
  { key: 'companies', Icon: Building2 },
  { key: 'contests', Icon: Trophy },
] as const;

function Stat({ target, label, suffix }: { target: number; label: string; suffix?: string }) {
  const { value, ref } = useCountUp(target);
  const { lang } = useLocalized();
  return (
    <div className="text-center">
      <div ref={ref} className="tnum text-3xl font-bold sm:text-4xl">
        {formatNumber(value, lang)}
        {suffix}
      </div>
      <div className="mt-1 text-sm text-content-muted">{label}</div>
    </div>
  );
}

export function Home() {
  const { t } = useTranslation();
  const { l } = useLocalized();
  // Prerender: mavzu sanoqlari HTML ichida keladi (skeletonsiz hydration)
  const initial = useInitialData();
  const [topicCounts, setTopicCounts] = useState<Partial<Record<ApiProblemTopic, number>> | null>(
    () => (initial?.topicCounts ? toCountMap(initial.topicCounts) : null),
  );
  const [stats, setStats] = useState<PublicStatsResponse | null>(initial?.stats ?? null);

  useSeo({
    title: 'SQLMaster.uz — SQL masalalar va amaliyot platformasi',
    description:
      "SQL o'rganish va amaliyot platformasi: real jadvallar asosidagi PostgreSQL masalalarini yeching, yechimni onlayn tekshiring, musobaqalarda qatnashing.",
    path: '/',
  });

  useEffect(() => {
    let cancelled = false;
    problemsApi
      .topics()
      .then((res) => {
        if (!cancelled) setTopicCounts(toCountMap(res.data));
      })
      .catch(() => {
        if (!cancelled) setTopicCounts((prev) => prev ?? {});
      });

    statsApi
      .overview()
      .then((res) => {
        if (!cancelled) setStats(res.data);
      })
      .catch(() => {
        /* stats yuklanmasa — tayllar 0 ko'rsatadi */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="hero-gradient relative overflow-hidden">
        <div className="mx-auto max-w-8xl px-4 pb-24 pt-16 sm:px-6 lg:px-8 lg:pb-28 lg:pt-24">
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-brand-500/25 bg-brand-500/10 px-3 py-1.5 text-sm font-medium text-brand-600 dark:text-brand-300">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-500" />
              </span>
              {t('home.badge')}
            </div>

            <h1 className="mb-6 text-4xl font-extrabold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
              {t('home.heroTitlePre')} <span className="text-gradient">{t('home.heroTitleHighlight')}</span>{' '}
              {t('home.heroTitlePost')}
            </h1>

            <p className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-content-secondary sm:text-xl">
              {t('home.heroSubtitle')}
            </p>

            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link to="/problems" className="btn btn-primary w-full gap-2 px-8 py-3.5 text-base font-semibold shadow-lg sm:w-auto">
                <Zap className="h-5 w-5" />
                {t('home.ctaPrimary')}
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm text-content-muted">
              {[t('home.proofProblems'), t('home.proofDialects')].map((txt) => (
                <div key={txt} className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-[var(--easy)]" />
                  {txt}
                </div>
              ))}
            </div>
          </div>

          {/* Preview card */}
          <div className="mx-auto mt-16 max-w-5xl">
            <div className="overflow-hidden rounded-2xl border border-line bg-elevated shadow-lg">
              <div className="flex items-center gap-2 border-b border-line bg-surface px-4 py-3">
                <div className="flex gap-1.5">
                  <div className="h-3 w-3 rounded-full bg-red-500/80" />
                  <div className="h-3 w-3 rounded-full bg-yellow-500/80" />
                  <div className="h-3 w-3 rounded-full bg-green-500/80" />
                </div>
                <div className="flex-1 text-center">
                  <span className="font-mono text-xs text-content-muted">sqlmaster.uz/problems/uzum-category-revenue</span>
                </div>
              </div>

              <div className="grid min-h-[320px] lg:grid-cols-2">
                <div className="border-b border-line p-6 lg:border-b-0 lg:border-r">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="badge-medium rounded px-2.5 py-0.5 text-xs font-semibold">
                      {t('difficulty.medium')}
                    </span>
                    <span className="text-sm text-content-muted">#12</span>
                  </div>
                  <h3 className="mb-3 text-lg font-semibold">{t('home.previewTitle')}</h3>
                  <p className="mb-4 text-sm leading-relaxed text-content-secondary">{t('home.previewDesc')}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="tag">JOIN</span>
                    <span className="tag">AGGREGATE</span>
                    <span className="tag">Uzum</span>
                  </div>
                </div>

                <div className="bg-[#0d1117] p-4 font-mono text-sm">
                  <div className="mb-3 flex items-center gap-2 text-xs text-slate-500">
                    <span className="text-emerald-400">●</span> main.sql
                  </div>
                  <pre className="leading-relaxed text-slate-300">
<span className="text-[#c792ea]">SELECT</span>{'\n'}  p.category,{'\n'}  <span className="text-[#c792ea]">SUM</span>(oi.quantity * oi.unit_price) <span className="text-[#c792ea]">AS</span> revenue{'\n'}<span className="text-[#c792ea]">FROM</span> order_items oi{'\n'}<span className="text-[#c792ea]">JOIN</span> products p <span className="text-[#c792ea]">ON</span> p.id = oi.product_id{'\n'}<span className="text-[#c792ea]">GROUP BY</span> p.category{'\n'}<span className="text-[#c792ea]">HAVING</span> <span className="text-[#c792ea]">SUM</span>(oi.quantity * oi.unit_price) &gt; 10000000{'\n'}<span className="text-[#c792ea]">ORDER BY</span> revenue <span className="text-[#c792ea]">DESC</span>;
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-line bg-elevated py-16">
        <div className="mx-auto max-w-8xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
            <Stat target={stats?.problems ?? 0} label={t('home.stats.problems')} />
            <Stat target={stats?.users ?? 0} label={t('home.stats.students')} />
            <Stat target={stats?.contests ?? 0} label={t('home.stats.contests')} />
            <Stat target={stats?.submissions ?? 0} label={t('home.stats.submissions')} />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24">
        <div className="mx-auto max-w-8xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-bold sm:text-4xl">{t('home.featuresTitle')}</h2>
            <p className="text-lg text-content-secondary">{t('home.featuresSubtitle')}</p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(({ key, Icon }) => (
              <Card key={key} hover className="p-6">
                <div className="feature-icon mb-4">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">{t(`home.features.${key}.title`)}</h3>
                <p className="text-sm leading-relaxed text-content-secondary">
                  {t(`home.features.${key}.desc`)}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* TOPICS */}
      <section className="border-y border-line bg-elevated py-24">
        <div className="mx-auto max-w-8xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-3 text-3xl font-bold">{t('home.topicsTitle')}</h2>
            <p className="text-content-secondary">{t('home.topicsSubtitle')}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {TOPICS.map((topic) => {
              const count = topicCounts?.[topic.slug];
              return (
                <Link
                  key={topic.slug}
                  to={topicPath(topic.slug)}
                  className="card card-hover group p-4 text-center"
                >
                  <topic.icon className="mx-auto mb-2 h-6 w-6 text-brand-600" aria-hidden />
                  <div className="font-medium transition-colors group-hover:text-brand-600">
                    {l(topic.title)}
                  </div>
                  <div className="mt-1 text-xs text-content-muted">
                    {topicCounts === null ? (
                      <span className="inline-block h-3 w-12 animate-pulse rounded bg-surface" />
                    ) : (
                      t('common.problemsCount', { count: count ?? 0 })
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="mb-4 text-3xl font-bold sm:text-4xl">{t('home.ctaTitle')}</h2>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-content-secondary">{t('home.ctaSubtitle')}</p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link to="/register" className="btn btn-primary gap-2 px-8 py-3.5 text-base font-semibold">
              {t('home.ctaButton')}
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to="/problems" className="btn btn-secondary px-8 py-3.5 text-base font-semibold">
              {t('home.ctaSecondaryButton')}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
